// src/pages/ClientOrganizations/ClientOrgList.jsx
// -----------------------------------------------------------------------------
// Admin/PM screen for managing Client Organizations using reusable components.
// Performs full CRUD (Read, Create, Update, Delete) locally or via clientOrgService.
// -----------------------------------------------------------------------------
import clientOrgService from '../../api/clientOrgService';
import useTableData from '../../hooks/useTableData';
import TableList from '../../components/common/TableList';
import TableForm from '../../components/common/TableForm';
import DeleteConfirmModal from '../../components/common/DeleteConfirmModal';
import StatusBadge from '../../components/common/StatusBadge';

// Schema configuration for Client_Organization_TBL
const CLIENT_ORG_FIELDS = [
  {
    name: 'clientOrgID',
    label: 'ID',
    type: 'integer',
    isPrimaryKey: true,
    readOnly: true,
  },
  {
    name: 'orgName',
    label: 'Organization Name',
    type: 'varchar',
    required: true,
    maxLength: 150,
    placeholder: 'e.g. Acme Corp',
  },
  {
    name: 'address',
    label: 'Address',
    type: 'textarea',
    maxLength: 255,
    placeholder: 'Enter physical address...',
  },
  {
    name: 'contactEmail',
    label: 'Contact Email',
    type: 'email',
    required: true,
    maxLength: 100,
    placeholder: 'e.g. contact@company.com',
  },
  {
    name: 'contactPhone',
    label: 'Contact Phone',
    type: 'phone',
    maxLength: 20,
    placeholder: 'e.g. +1 (555) 019-9000',
  },
  {
    name: 'status',
    label: 'Status',
    type: 'select',
    required: true,
    defaultValue: 'Active',
    options: [
      { label: 'Active', value: 'Active' },
      { label: 'Inactive', value: 'Inactive' },
    ],
  },
];

export default function ClientOrgList() {
  const {
    data,
    paginatedData,
    loading,
    submitting,
    deleting,
    serverErrors,
    searchTerm,
    setSearchTerm,
    sortConfig,
    handleSort,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    totalRecords,
    isFormOpen,
    isDeleteOpen,
    formMode,
    selectedRecord,
    openCreateModal,
    openEditModal,
    closeFormModal,
    openDeleteModal,
    closeDeleteModal,
    handleSave,
    handleDelete,
  } = useTableData(clientOrgService, {
    idKey: 'clientOrgID',
    itemName: 'Client Organization',
    initialSort: { key: 'clientOrgID', direction: 'asc' },
  });

  const columns = [
    { key: 'clientOrgID', label: 'ID', sortable: true, width: '80px' },
    { key: 'orgName', label: 'Organization Name', sortable: true },
    { key: 'contactEmail', label: 'Contact Email', sortable: true },
    { key: 'contactPhone', label: 'Contact Phone', sortable: true },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (row) => <StatusBadge value={row.status} />,
    },
  ];

  return (
    <div>
      <div className="page-header" style={{ marginBottom: '20px' }}>
        <div>
          <h1>Client Organizations</h1>
          <div className="subtitle" style={{ color: 'var(--color-text-muted)', fontSize: '13px', marginTop: '4px' }}>
            External client companies with access to the system.
          </div>
        </div>
        <button className="btn btn-primary" onClick={openCreateModal}>
          + Add Organization
        </button>
      </div>

      {/* Reusable Table List */}
      <TableList
        columns={columns}
        data={data}
        paginatedData={paginatedData}
        loading={loading}
        idKey="clientOrgID"
        emptyMessage="No client organizations found."
        // Search & Filter
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        // Sorting
        sortConfig={sortConfig}
        onSort={handleSort}
        // Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalRecords={totalRecords}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        // Actions
        onEdit={openEditModal}
        onDelete={openDeleteModal}
      />

      {/* Dynamic Form Modal */}
      {isFormOpen && (
        <TableForm
          fields={CLIENT_ORG_FIELDS}
          initialValues={selectedRecord || { orgName: '', address: '', contactEmail: '', contactPhone: '', status: 'Active' }}
          mode={formMode}
          isOpen={isFormOpen}
          onSubmit={handleSave}
          onCancel={closeFormModal}
          submitting={submitting}
          serverErrors={serverErrors}
          itemName="Client Organization"
        />
      )}

      {/* Confirmation Delete Modal */}
      <DeleteConfirmModal
        isOpen={isDeleteOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        title="Delete Organization"
        itemLabel={selectedRecord ? selectedRecord.orgName : 'Organization'}
        deleting={deleting}
      />
    </div>
  );
}